import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Peças comuns dos gráficos do painel (SVG à mão, sem lib — padrão do
 * PeakHoursChart). Regras do skill de dataviz: marcas finas, grade em
 * hairline recessiva, texto sempre na cor de texto (nunca na cor da série),
 * legenda para 2+ séries, tooltip no hover E no foco por teclado/toque.
 */

export const INK = '#001B74';
export const INK_MUTED = 'rgba(0,27,116,0.5)';
export const GRID = 'rgba(0,27,116,0.08)';
export const SURFACE = '#FFFFFF';

/** Largura do container, pra desenhar o SVG em pixels reais (texto sem distorção). */
export function useElementWidth<T extends HTMLElement>(fallback = 320) {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth || fallback);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, width] as const;
}

/** Teto "redondo" do eixo (1, 2, 2.5, 5 × 10^n) — ticks limpos. */
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (v <= m * exp) return m * exp;
  }
  return 10 * exp;
}

const compact = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });
export function compactNumber(v: number): string {
  return Math.abs(v) >= 1000 ? compact.format(v) : String(Math.round(v * 10) / 10).replace('.', ',');
}

/** Caminho de barra com 4px arredondados SÓ no topo (base reta no eixo). */
export function topRoundedBar(x: number, y: number, w: number, h: number, r = 4): string {
  const rr = Math.min(r, w / 2, h);
  if (h <= 0) return '';
  return [
    `M${x},${y + h}`,
    `V${y + rr}`,
    `Q${x},${y} ${x + rr},${y}`,
    `H${x + w - rr}`,
    `Q${x + w},${y} ${x + w},${y + rr}`,
    `V${y + h}`,
    'Z'
  ].join(' ');
}

export interface LegendItem {
  label: string;
  color: string;
  note?: string;
  /** Chave da marca deste item; sem valor, vale o `kind` da legenda. */
  kind?: 'rect' | 'line' | 'dash';
}

/** Legenda: chave que imita a marca (retângulo p/ barras, traço p/ linhas, tracejado p/ comparativo). */
export function Legend({ items, kind = 'rect' }: { items: LegendItem[]; kind?: 'rect' | 'line' }) {
  if (items.length < 2) return null;
  return (
    <ul className="m-0 mt-3 flex list-none flex-wrap gap-x-4 gap-y-1.5 p-0 text-[12px] text-bow-royal/70">
      {items.map((it) => {
        const k = it.kind ?? kind;
        return (
          <li key={it.label} className="inline-flex items-center gap-1.5">
            {k === 'dash' ? (
              <svg aria-hidden width={16} height={4}>
                <line x1={0} x2={16} y1={2} y2={2} stroke={it.color} strokeWidth={2} strokeDasharray="4 3" />
              </svg>
            ) : (
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: k === 'rect' ? 10 : 14,
                  height: k === 'rect' ? 10 : 2,
                  borderRadius: k === 'rect' ? 3 : 1,
                  background: it.color
                }}
              />
            )}
            {it.label}
            {it.note && <span className="text-bow-royal/45">({it.note})</span>}
          </li>
        );
      })}
    </ul>
  );
}

/** Traço da série "período anterior": tinta recessiva, tracejada — nunca uma cor de categoria. */
export const PREVIOUS_INK = 'rgba(0,27,116,0.55)';

export interface TooltipRow {
  label: string;
  value: string;
  color?: string;
}

/** Caixa do tooltip: valor em destaque, nome da série secundário, chave em traço. */
export function TooltipBox({
  title,
  rows,
  x,
  width
}: {
  title: ReactNode;
  rows: TooltipRow[];
  x: number;
  width: number;
}) {
  const boxW = 176;
  const left = Math.max(0, Math.min(x - boxW / 2, width - boxW));
  return (
    <div
      role="status"
      className="pointer-events-none absolute top-0 z-10 rounded-xl bg-white px-3 py-2 text-[12px] shadow-deep"
      style={{ left, width: boxW }}
    >
      <div className="mb-1 font-semibold text-bow-royal/60">{title}</div>
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-2">
          {r.color && (
            <span aria-hidden style={{ width: 10, height: 2, borderRadius: 1, background: r.color }} />
          )}
          <span className="font-semibold tabular-nums text-bow-royal">{r.value}</span>
          <span className="truncate text-bow-royal/55">{r.label}</span>
        </div>
      ))}
    </div>
  );
}
